import { FormEvent, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useGetAllServices } from '../../../apis/services/get-all-services';
import { useCancelService } from '../../../apis/services/cancel-service';
import { areBothDatesPresent, formatDate, isOneDatePresent } from '../../../utils/helpers';

type PropTypes = {
    userId?: string;
    driverId?: string;
};

type FormType = {
    fromDate: string;
    toDate: string;
    type: string;
    status: string;
    [key: string]: string;
};

const initialState: FormType = {
    fromDate: "",
    toDate: "",
    type: "",
    status: "",
};

const useServicesTable = ({ userId, driverId }: PropTypes) => {
    const [formState, setFormState] = useState<FormType>(initialState);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("");
    const [type, setType] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedServiceId, setSelectedServiceId] = useState("");
    const [selectedFeedback, setSelectedFeedback] = useState<{
        experienceRating: null | number | string;
        feedback: null | string;
        orderId: string;
    }>({ experienceRating: null, feedback: null, orderId: "" });

    const form = useRef<HTMLFormElement>(null);

    const { data, isFetching: loading, refetch } = useGetAllServices({
        users: userId,
        drivers: driverId,
        page,
        fromdate: formState.fromDate,
        todate: formState.toDate,
        type: formState.type,
        limit: 10,
        status: formState.status,
    });

    const { mutateAsync: cancelService, isPending: isCancelingService } = useCancelService();

    const handleServiceCancel = async () => {
        try {
            await cancelService(selectedServiceId);
            toast.success("Service Cancelled Successfully");
            refetch();
        } catch (error) {
            toast.error("Couldn't cancel service at this moment. Please try again later.");
        } finally {
            setSelectedServiceId("");
        }
    };

    const handleApply = (e: FormEvent) => {
        e.preventDefault();
        setPage(1);
        setFormState({ fromDate, toDate, status, type });
    };

    const handleReset = () => {
        form.current?.reset();
        setFormState(initialState);
        setStatus("");
        setFromDate("");
        setToDate("");
        setType("");
        setPage(1);
    };

    const handleFromDate = (e: Date | null | undefined) =>
        setFromDate(formatDate(e?.toString() || ""));

    const handleToDate = (e: Date | null | undefined) =>
        setToDate(formatDate(e?.toString() || ""));

    const isAnyFilterApplied = () => status !== "" || type !== "" || fromDate !== "" || toDate !== "";

    const isApplyButtonDisabled = () => {
        if (isAnyFilterApplied()) return isOneDatePresent(fromDate, toDate);
        return !areBothDatesPresent(fromDate, toDate);
    };

    const isClearButtonDisabled = () =>
        status === "" && type === "" && fromDate === "" && toDate === "";

    return {
        form,
        services: data?.docs ?? [],
        totalPages: data?.totalPages ?? 0,
        loading,
        page,
        setPage,
        status,
        setStatus,
        type,
        setType,
        fromDate,
        toDate,
        handleFromDate,
        handleToDate,
        selectedServiceId,
        setSelectedServiceId,
        selectedFeedback,
        setSelectedFeedback,
        isCancelingService,
        handleServiceCancel,
        handleApply,
        handleReset,
        isApplyButtonDisabled,
        isClearButtonDisabled,
    };
};

export default useServicesTable;